import PendingCache from "./PendingCache.mts";
import UArray from "./UArray.mts";
import UObject from "./UObject.mts";
import UProxy from "./UProxy.mts";

namespace Log {
  export let enabled = true;
  export let level = "info" as "info" | "debug";

  export const plain = (...args: any[]) => enabled && console.log(...args);
  export const warn = (...args: any[]) => enabled && console.warn(...args);
  export const error = (...args: any[]) => enabled && console.error(...args);

  export const group =
    (...args: any[]) =>
    (group: () => void) => {
      if (!enabled) return;

      console.groupCollapsed(...resolveConsoleArgs(...args));
      group();
      console.groupEnd();
    };

  export const color = (color: string, content: any) => [
    Log.color,
    color,
    content,
  ];
  export const style = (styles: string, content: any) => [
    Log.style,
    styles,
    content,
  ];
  export const raw = (value: any) => [Log.raw, value];
  export const join = (arr: any[], separator: any) => [
    Log.join,
    arr,
    separator,
  ];
  export const args = (args: any[]) => Log.join(args.map(Log.raw), ", ");
  export const fnPath = (path: (UObject.Key | any[])[]) =>
    Log.join(
      path.map((item, i, { length }) =>
        typeof item === "object"
          ? item
          : Log.color(
              i === length - 1 ? "#fe6" : i === 0 ? "#4af" : "#bef",
              item
            )
      ),
      Log.color("grey", ".")
    );
  export const fn = (path: (UObject.Key | any[])[], args: any[]) =>
    Log.join([Log.fnPath(path), "(", Log.args(args), ")"], "");

  export function resolveConsoleArgs(...args: any[]) {
    if (
      !(
        Array.isArray(args[0]) &&
        "raw" in args[0] &&
        Array.isArray(args[0].raw)
      )
    )
      return args;

    const interpolations: any[] = [];
    return [
      UArray.joinWith(args[0], (i) =>
        (function resolve(value = args[i + 1]): string {
          if (typeof value !== "object") return value;
          if (typeof value === "symbol") return String(value);
          if (Array.isArray(value)) {
            if (value[0] === Log.color) {
              interpolations.push(`color:${value[1]}`, "");
              return `%c${String(value[2])}%c`;
            }
            if (value[0] === Log.style) {
              interpolations.push(value[1], "");
              return `%c${String(value[2])}%c`;
            }
            if (value[0] === Log.join) {
              return UArray.zip(
                value[1],
                Array(Math.max(value[1].length - 1, 0)).fill(value[2])
              )
                .map(resolve)
                .join("");
            }
            if (value[0] === Log.raw) {
              interpolations.push(value[1]);
              return "%o";
            }
          }
          interpolations.push(value);
          return "%o";
        })()
      ),
      ...interpolations,
    ];
  }
  export function withTrace<T>(...args: [...any[], T]): T {
    if (Log.level === "debug")
      group(...args)(() => {
        console.trace();
      });
    else plain(...resolveConsoleArgs(...args));

    return args.at(-1);
  }
  export function v<T>(...args: [...any[], T]): T {
    plain(...resolveConsoleArgs(...args));
    return args.at(-1);
  }
  export function r<T extends () => any>(
    ...args: [...any[], T]
  ): ReturnType<T> {
    const f = args.pop();
    const res = f();
    plain(...args, f.toString(), "=>", res);
    return res;
  }

  export function deepDecorate<T extends UObject.Any>(
    subject: T,
    identifier: string,
    prefix: string | ReturnType<typeof join> = ""
  ) {
    if (!enabled) return subject;
    return UProxy.deepDecorate(subject, (original, path) => (...args) => {
      Log.withTrace`${prefix}${Log.fn([identifier, ...path], args)}`;

      const res = original(...args);

      if (Log.enabled && res?.[PendingCache.cacheHit])
        Log.v`${Log.color("grey", "CACHE-HIT")} ${
          // Prevent PendingCache.cacheHit symbol from bloating logs
          new Promise((resolve) => resolve(res))
        }`;

      return res;
    }) as typeof subject;
  }
}

export default Log;
